import { NgClass } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { MatExpansionModule } from '@angular/material/expansion';
import { faAddressBook } from '@fortawesome/free-solid-svg-icons';
import { NgxSpinnerService } from 'ngx-spinner';
import { Observable } from 'rxjs';
import { FormMode } from 'src/app/controls/common/form-mode';
import { DefaultManagementComponent } from 'src/app/controls/forms/default-management.component';
import { HttpError } from 'src/app/core/http-error';
import { Contact } from 'src/app/models/contact';
import { Rights } from 'src/app/models/enums/rights';
import { IContact } from 'src/app/models/interfaces/contact.interface';
import { ContactsService } from 'src/app/services/contacts.service';
import { MemoryService } from 'src/app/services/memory.service';
import { NotificationService } from 'src/app/services/notification-service';
import { DetailHeaderComponent } from '../../controls/detail-header/detail-header.component';
import { ListHeaderComponent } from '../../controls/list-header/list-header.component';
import { ContactDetailContext } from './contact-detail-context';
import { ContactDetailComponent } from './contact-detail/contact-detail.component';
import { ContactsListComponent } from './contacts-list/contacts-list.component';

enum DisplayMode {
    List,
    Form,
}
@Component({
    selector: 'app-contacts',
    templateUrl: './contacts.component.html',
    styleUrls: ['./contacts.component.scss'],
    imports: [MatExpansionModule, NgClass, ListHeaderComponent, ContactsListComponent, DetailHeaderComponent, ContactDetailComponent],
})
export class ContactsComponent extends DefaultManagementComponent<IContact, Contact, number> implements OnInit {
    /**
     * Liste des icônes utilisés dans le template
     */
    public faContact = faAddressBook;

    /**
     * Liste des enums utilisés dans le template
     */
    public override FormMode = FormMode;
    public override DisplayMode = DisplayMode;
    /**
     * Contexte par défaut du formulaire contenant les données invariantes entre contacts.
     */
    public defaultContext: ContactDetailContext = new ContactDetailContext();

    /**
     * Indique si le bouton d'ajout/Edit doit apparaitre.
     */
    public displayAddbuttom = true;
    public displayEditbuttom = true;

    /**
     * Constructeur.
     * @param notificationService : Service utilisé pour gérer l'affichage des notifications.
     * @param ContactsService : Service métier utilisé pour gérer les contacts.
     */
    constructor(
        protected override notificationService: NotificationService,
        private ContactsService: ContactsService,
        private memoryService: MemoryService,
        private spinner: NgxSpinnerService
    ) {
        super(notificationService);
    }

    /**
     * Appellé lors de l'initialisation du composant.
     */
    ngOnInit() {
        this.displayAddbuttom = !!this.memoryService.getConnectedUser().hasRight(Rights.ContactCreate);
        this.displayEditbuttom = !!this.memoryService.getConnectedUser().hasRight(Rights.ContactEdit);
    }

    /**
     * Appellé lorsque l'utilisateur selectionne un contact.
     * @param contact Contact selectionné.
     */
    public onContactSelected(contact: Contact) {
        this.spinner.show('main-spinner');
        this.createContext();
        this.loadContact(contact.id).subscribe(
            (x) => {
                this.refresh(Contact.createFormCopy(x), FormMode.Reading);
            },
            (error: HttpError) => {
                this.notificationService.displayError('Erreur', "Impossible d'afficher le contact." + error.message);
                this.spinner.hide('main-spinner');
            }
        );
    }

    /**
     * Appellé lorsque l'utilisateur clique sur l'icône 'Retour à la liste'.
     */
    public onBackToList() {
        this.setDisplayMode(DisplayMode.List);
    }

    /**
     * Appellé lorsque l'utilisateur sélectionne 'Consulter' dans le menu contextuel.
     * @param contact contact sélectionné.
     */
    public onContactDisplayed(contact: Contact) {
        this.spinner.show('main-spinner');
        this.createContext();
        this.loadContact(contact.id).subscribe(
            (x) => {
                this.refresh(Contact.createFormCopy(x), FormMode.Reading);
            },
            (error: HttpError) => {
                this.notificationService.displayError('Erreur', "Impossible d'afficher le contact." + error.message);
                this.spinner.hide('main-spinner');
            }
        );
    }

    /**
     * Appellé lorsque l'utilisateur sélectionne 'Editer' dans le menu contextuel.
     * @param contact contact sélectionné.
     */
    public onContactEdited(contact: Contact) {
        this.spinner.show('main-spinner');
        this.createContext();
        // Cas de la mise à jour.
        this.loadContact(contact.id).subscribe(
            (x) => {
                this.refresh(Contact.createFormCopy(x), FormMode.Edition);
            },
            (error: HttpError) => {
                this.notificationService.displayError('Erreur', "Impossible d'éditer le contact." + error.message);
                this.spinner.hide('main-spinner');
            }
        );
    }

    /**
     * Appellé lorsque l'utilisateur clique sur le bouton 'Créer'.
     */
    public onContactCreated() {
        this.displayForm(Contact.createFormCopy(new Contact()), FormMode.Creation);
    }

    /**
     * Appellé lorsque l'utilisateur sélectionne 'Dupliquer' dans le menu contextuel.
     * @param contact contact sélectionné.
     */
    public onContactDuplicated(contact: Contact) {
        let duplicatedContact = Contact.createFormCopy(contact);
        duplicatedContact.id = null;
        this.displayForm(duplicatedContact, FormMode.Creation);
    }

    /**
     * Appellé lorsque le contenu du formulaire a été sauvegardé.
     * @param Contact : Valeur du contact retourné par le formulaire.
     */
    public contactUpdated(contact: Contact): void {
        this.spinner.show('main-spinner');
        if (this.context.editedItemId) {
            // Cas de la mise à jour.
            this.ContactsService.update(this.context.editedItemId, contact).subscribe(
                (x) => {
                    this.refreshWithNotification(Contact.createFormCopy(x), 'Contact mis à jour', FormMode.Reading);
                },
                (error: HttpError) => {
                    this.notificationService.displayError('Erreur', 'Impossible de mettre à jour le contact.' + error.message);
                    this.spinner.hide('main-spinner');
                }
            );
        } else {
            // Cas de la création.
            this.ContactsService.add(contact).subscribe(
                (x) => {
                    this.refreshWithNotification(Contact.createFormCopy(x), 'Contact créé', FormMode.Reading);
                },
                (error: HttpError) => {
                    this.notificationService.displayError('Erreur', 'Impossible de créer le contact.' + error.message);
                    this.spinner.hide('main-spinner');
                }
            );
        }
    }

    /**
     * Appellé lorsque l'utilisateur sélectionne 'Supprimer' dans le menu contextuel.
     * @param contact contact sélectionné.
     */
    public onContactDeleted(contact: Contact) {
        this.spinner.show('main-spinner');
        // Suppression effective du contact.
        this.ContactsService.delete(contact.id).subscribe(
            (x) => {
                this.notificationService.displaySuccess(null, 'Contact supprimé');
                this.itemsList.refresh();
                this.spinner.hide('main-spinner');
            },
            (error: HttpError) => {
                this.notificationService.displayError('Erreur', 'Impossible de supprimer le contact.' + error.message);
                this.spinner.hide('main-spinner');
            }
        );
    }

    /**
     * Appellé lors de l'export
     */
    public onContactsExported() {
        this.createContext();
        this.itemsList.export('Contacts.xlsx');
    }

    /**
     * Appellé lors de l'import
     */
    public onContactsImported() {
        this.itemsList.refresh();
    }

    /**
     * Appellé lors de l'aide
     */
    public onContactsHelped() {
        this.itemsList.displayHelpDialog('contacts');
    }

    /**
     * Charge les données d'un contact.
     * @param ContactId : Id du contact à charger.
     */
    public loadContact(contactId: number): Observable<Contact> {
        return this.ContactsService.get(contactId);
    }

    /**
     * Pré-rempli les champs du formulaire.
     * @param Contact : Valeur utilisée pour pré-remplir les champs du formulaire.
     */
    protected fillForm(Contact: IContact): ContactDetailContext {
        let context = this.createContext();
        context.formContext.item = Contact;
        context.editedItemId = Contact.id;
        return context;
    }

    /**
     * Créer un nouveau contexte.
     */
    protected createContext(): ContactDetailContext {
        let context = new ContactDetailContext();
        return context;
    }
}
